// L12 native Ajax helper for the lead inbox and call settings. Every write
// carries the session CSRF token and a fresh Idempotency-Key; versioned
// writes carry the exact If-Match the server issued. Failures are returned,
// never thrown, so callers can refuse to show success after a failed request.

export function newIdempotencyKey() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export function versionTag(version) {
  return `"v${version}"`
}

// One key per user intent: the same write (method, URL, precondition and
// body) reuses its key until the server gives a definite answer, so a retry
// after a lost response can be recognized instead of acting twice.
const intentKeys = new Map()

export async function apiRequest(url, { method = 'GET', body, etag, csrfToken } = {}) {
  const headers = { Accept: 'application/json' }
  const options = { credentials: 'same-origin', headers }
  let intent = null
  if (method !== 'GET') {
    options.body = JSON.stringify(body || {})
    intent = JSON.stringify([method, url, etag || null, options.body])
    if (!intentKeys.has(intent)) intentKeys.set(intent, newIdempotencyKey())
    headers['Content-Type'] = 'application/json'
    headers['X-CSRF-Token'] = csrfToken || ''
    headers['Idempotency-Key'] = intentKeys.get(intent)
    options.method = method
  }
  if (etag) headers['If-Match'] = etag
  let response
  try {
    response = await fetch(url, options)
  } catch (error) {
    return { ok: false, status: 0, data: null, meta: null, error: { code: 'network_error' }, etag: null }
  }
  if (intent) intentKeys.delete(intent)
  let payload = null
  try {
    payload = await response.json()
  } catch (error) {
    payload = null
  }
  return {
    ok: response.ok,
    status: response.status,
    data: payload?.data ?? null,
    meta: payload?.meta ?? null,
    error: payload?.error ?? (response.ok ? null : { code: 'request_failed' }),
    etag: response.headers?.get?.('ETag') ?? null
  }
}

export function humanize(code) {
  if (!code) return ''
  const text = String(code).replace(/_/g, ' ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

// Status 0 means the request may or may not have reached the server.
export function outcomeUnknown(result) {
  return result.status === 0
}
